// src/pages/TradingData/IndicatorCard.jsx
// Beautiful Matrix Grid style indicator card — used inside the picker modal
// In filter mode, generates BOTH preceding (selected dir) and exclude (opposite dir) signals

import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Stack, Paper, Chip, alpha, Select, MenuItem, FormControl } from '@mui/material';
import { MONO, ASSETS, TIMEFRAMES, TIMEFRAME_LABELS, SESSIONS } from './IndicatorConfigs';

const RowLabel = ({ children }) => (
  <Typography sx={{
    color: alpha('#fff', 0.25), fontSize: '0.6rem', fontWeight: 700,
    letterSpacing: '0.2em', textTransform: 'uppercase', fontFamily: MONO, mb: 0.8,
  }}>
    {children}
  </Typography>
);

const OptionBtn = ({ active, color, children, onClick }) => (
  <Button onClick={onClick} sx={{
    flex: 1, py: 1.1, fontSize: '0.82rem', fontWeight: 700, fontFamily: MONO,
    border: '1px solid', borderRadius: 1.5, textTransform: 'none', transition: 'all 0.2s',
    borderColor: active ? color : alpha('#fff', 0.06),
    bgcolor: active ? alpha(color, 0.12) : 'transparent',
    color: active ? color : alpha('#fff', 0.25),
    '&:hover': { borderColor: alpha(color, 0.5), bgcolor: alpha(color, 0.08), color },
  }}>
    {children}
  </Button>
);


export default function IndicatorCard({ indicatorKey, config, onSelect, roleColor, filterMode = false, companionMode = false, defaults = {} }) {
  const [direction, setDirection] = useState('LONG');
  const [crossDir, setCrossDir] = useState('ABOVE');
  const [sensitivity, setSensitivity] = useState(config.defaultSensitivity || 15);
  const [mode, setMode] = useState(config.defaultMode || 'Medium');
  const [length, setLength] = useState(config.defaultLength || 20);
  const [vgbType, setVgbType] = useState(config.defaultType || 1);
  const [period, setPeriod] = useState(config.defaultPeriod || 75);
  const [version, setVersion] = useState(config.defaultVersion || 2);
  const [setting, setSetting] = useState(config.defaultSetting || 'DEFAULT');
  const [scalp, setScalp] = useState(config.defaultScalp || 'OFF');

  // Per-companion overrides (default to parent's global settings)
  const [cTimeframe, setCTimeframe] = useState(defaults.timeframe || '1m');
  const [cAsset, setCAsset] = useState(defaults.asset || 'NQ');
  const [cSession, setCSession] = useState(defaults.session || 'ETH');

  // Build signal for the SELECTED direction/crossDir
  const getSignal = (overrideDir, overrideCross) => {
    const dir = overrideDir || direction;
    const cross = overrideCross || crossDir;
    switch (indicatorKey) {
      case 'COBRA': return config.buildSignal(dir, sensitivity, mode, '1m');
      case 'VGB': return config.buildSignal(dir, length, vgbType);
      case 'SMART_BAND': case 'TRIDENT_SCALP': return config.buildSignal(dir, sensitivity);
      case 'SMART_TRAIL': return config.buildSignal(dir, version);
      case 'ALGO_PRO': case 'TARGET': return config.buildSignal(dir);
      case 'ALGO_ALPHA': return config.buildSignal(dir, setting, scalp);
      case 'MACD': return config.buildSignal(dir, mode);
      case 'EMA': return config.buildSignal(cross, period);
      case 'LUX': {
        const sens = config.sensitivities[dir] || [15];
        const useSens = sens.includes(sensitivity) ? sensitivity : config.defaultSensitivity;
        return config.buildSignal(dir, useSens);
      }
      default: return 'UNKNOWN';
    }
  };

  // Build the OPPOSITE signal (for filter exclude)
  const getOppositeSignal = () => {
    const oppDir = direction === 'LONG' ? 'SHORT' : 'LONG';
    const oppCross = crossDir === 'ABOVE' ? 'BELOW' : 'ABOVE';
    return getSignal(oppDir, oppCross);
  };

  const getSensitivities = () =>
    indicatorKey === 'LUX' ? (config.sensitivities[direction] || [15]) : (config.sensitivities || []);

  const sensitivities = getSensitivities();
  const signalName = getSignal();
  const excludeSignal = filterMode ? getOppositeSignal() : null;
  const ac = config.accent;

  useEffect(() => {
    if (indicatorKey === 'LUX' && sensitivities.length && !sensitivities.includes(sensitivity))
      setSensitivity(config.defaultSensitivity);
  }, [direction]);

  const handleSelect = () => {
    if (filterMode) {
      onSelect({
        preceding: signalName,
        exclude: excludeSignal,
        timeframe: cTimeframe,
        asset: cAsset,
        session: cSession,
      });
    } else {
      onSelect({ signal: signalName, timeframe: cTimeframe, asset: cAsset, session: cSession });
    }
  };

  return (
    <Box sx={{
      background: '#0a0e17', borderRadius: 3, p: 3,
      border: `1px solid ${alpha(ac, 0.12)}`, fontFamily: MONO,
      display: 'flex', flexDirection: 'column', gap: 2.2,
      position: 'relative', overflow: 'hidden',
      '&::before': {
        content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: 2,
        background: `linear-gradient(90deg, transparent 10%, ${ac} 50%, transparent 90%)`,
        opacity: 0.6,
      },
    }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: ac, boxShadow: `0 0 10px ${alpha(ac, 0.7)}` }} />
          <Typography sx={{ color: ac, fontSize: '0.72rem', letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 700 }}>
            {config.label}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.5}>
          {filterMode && (
            <Chip label="FILTER MODE" size="small" sx={{
              height: 18, fontSize: '0.48rem', fontWeight: 700,
              bgcolor: alpha('#f59e0b', 0.12), color: '#f59e0b',
              border: `1px solid ${alpha('#f59e0b', 0.3)}`,
              '& .MuiChip-label': { px: 0.6 },
            }} />
          )}
          {config.warning && (
            <Chip label={config.warning} size="small" sx={{
              height: 18, fontSize: '0.5rem', fontWeight: 600,
              bgcolor: alpha('#ef4444', 0.1), color: '#ef4444',
              border: `1px solid ${alpha('#ef4444', 0.25)}`, '& .MuiChip-label': { px: 0.8 },
            }} />
          )}
        </Stack>
      </Stack>

      {/* Direction */}
      {(config.hasDirection || indicatorKey === 'COBRA') && (
        <Box><RowLabel>{filterMode ? 'Filter Direction (preceding signal)' : 'Direction'}</RowLabel>
          <Stack direction="row" spacing={0.6}>
            {['LONG', 'SHORT'].map(d => (
              <OptionBtn key={d} active={direction === d} color={d === 'LONG' ? '#22c55e' : '#ef4444'} onClick={() => setDirection(d)}>{d}</OptionBtn>
            ))}
          </Stack>
        </Box>
      )}

      {/* Cross Direction (EMA) */}
      {config.hasCrossDirection && (
        <Box><RowLabel>{filterMode ? 'Filter Direction (preceding signal)' : 'Cross Direction'}</RowLabel>
          <Stack direction="row" spacing={0.6}>
            {['ABOVE', 'BELOW'].map(d => (
              <OptionBtn key={d} active={crossDir === d} color={d === 'ABOVE' ? '#22c55e' : '#ef4444'} onClick={() => setCrossDir(d)}>
                {d === 'ABOVE' ? '↑ ABOVE' : '↓ BELOW'}
              </OptionBtn>
            ))}
          </Stack>
        </Box>
      )}

      {/* Sensitivity */}
      {sensitivities.length > 0 && (
        <Box><RowLabel>Sensitivity</RowLabel>
          <Stack direction="row" spacing={0.6}>
            {sensitivities.map(s => <OptionBtn key={s} active={sensitivity === s} color={ac} onClick={() => setSensitivity(s)}>{s}</OptionBtn>)}
          </Stack>
        </Box>
      )}

      {/* EMA Period */}
      {config.periods && (
        <Box><RowLabel>EMA Period</RowLabel>
          <Stack direction="row" spacing={0.6}>
            {config.periods.map(p => <OptionBtn key={p} active={period === p} color={ac} onClick={() => setPeriod(p)}>{p}</OptionBtn>)}
          </Stack>
        </Box>
      )}

      {/* Cobra Mode */}
      {config.modes && (
        <Box><RowLabel>Signal Mode</RowLabel>
          <Stack direction="row" spacing={0.6}>
            {config.modes.map(m => <OptionBtn key={m} active={mode === m} color={ac} onClick={() => setMode(m)}>{m}</OptionBtn>)}
          </Stack>
        </Box>
      )}

      {/* VGB Length */}
      {config.lengths && (
        <Box><RowLabel>Length</RowLabel>
          <Stack direction="row" spacing={0.6}>
            {config.lengths.map(l => <OptionBtn key={l} active={length === l} color={ac} onClick={() => setLength(l)}>{l}</OptionBtn>)}
          </Stack>
        </Box>
      )}

      {/* VGB Type */}
      {config.types && (
        <Box><RowLabel>Type</RowLabel>
          <Stack direction="row" spacing={0.6}>
            {config.types.map(t => <OptionBtn key={t} active={vgbType === t} color={ac} onClick={() => setVgbType(t)}>{t}</OptionBtn>)}
          </Stack>
        </Box>
      )}

      {/* Version (Smart Trail) */}
      {config.versions && (
        <Box><RowLabel>Version</RowLabel>
          <Stack direction="row" spacing={0.6}>
            {config.versions.map(v => <OptionBtn key={v} active={version === v} color={ac} onClick={() => setVersion(v)}>{v}</OptionBtn>)}
          </Stack>
        </Box>
      )}

      {/* Setting (Algo Alpha) */}
      {config.settings && (
        <Box><RowLabel>Setting</RowLabel>
          <Stack direction="row" spacing={0.6}>
            {config.settings.map(s => <OptionBtn key={s} active={setting === s} color={ac} onClick={() => setSetting(s)}>{s}</OptionBtn>)}
          </Stack>
        </Box>
      )}

      {/* Scalp (Algo Alpha) */}
      {config.scalps && (
        <Box><RowLabel>Scalp</RowLabel>
          <Stack direction="row" spacing={0.6}>
            {config.scalps.map(s => <OptionBtn key={s} active={scalp === s} color={ac} onClick={() => setScalp(s)}>{s}</OptionBtn>)}
          </Stack>
        </Box>
      )}

      {/* Signal context: Timeframe / Asset / Session — shown in both
          signal mode and filter mode so the user always sees which
          (timeframe, asset, session) the selection applies to. */}
      <Box><RowLabel>Timeframe</RowLabel>
        <FormControl fullWidth size="small">
          <Select
            value={cTimeframe}
            onChange={(e) => setCTimeframe(e.target.value)}
            sx={{
              fontFamily: MONO, fontSize: '0.82rem', fontWeight: 700,
              color: ac, borderRadius: 1.5,
              '& .MuiOutlinedInput-notchedOutline': { borderColor: alpha(ac, 0.25) },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: alpha(ac, 0.5) },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ac },
              '& .MuiSelect-icon': { color: alpha(ac, 0.6) },
              bgcolor: alpha(ac, 0.06),
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  bgcolor: '#0d1117', border: `1px solid ${alpha(ac, 0.2)}`,
                  borderRadius: 2, mt: 0.5,
                },
              },
            }}
          >
            {TIMEFRAMES.map(tf => (
              <MenuItem key={tf} value={tf} sx={{
                fontFamily: MONO, fontSize: '0.82rem', fontWeight: 600,
                color: alpha('#fff', 0.7),
                '&.Mui-selected': { color: ac, bgcolor: alpha(ac, 0.1) },
                '&:hover': { bgcolor: alpha(ac, 0.07) },
              }}>
                {TIMEFRAME_LABELS[tf] || tf}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Box><RowLabel>Asset</RowLabel>
        <Stack direction="row" spacing={0.6}>
          {ASSETS.map(a => <OptionBtn key={a} active={cAsset === a} color={ac} onClick={() => setCAsset(a)}>{a}</OptionBtn>)}
        </Stack>
      </Box>
      <Box><RowLabel>Session</RowLabel>
        <Stack direction="row" spacing={0.6}>
          {SESSIONS.map(s => <OptionBtn key={s} active={cSession === s} color={ac} onClick={() => setCSession(s)}>{s}</OptionBtn>)}
        </Stack>
      </Box>

      {/* Output — Signal mode vs Filter mode */}
      {filterMode ? (
        <Paper elevation={0} sx={{
          bgcolor: alpha('#000', 0.5), border: `1px solid ${alpha(ac, 0.2)}`,
          borderRadius: 2, p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.2,
        }}>
          <Stack direction="row" spacing={2}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ color: alpha('#22c55e', 0.5), fontSize: '0.45rem', letterSpacing: '0.15em', fontFamily: MONO }}>PRECEDING (must exist before entry)</Typography>
              <Typography sx={{ color: '#22c55e', fontSize: '0.95rem', fontWeight: 800, fontFamily: MONO }}>{signalName}</Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ color: alpha('#ef4444', 0.5), fontSize: '0.45rem', letterSpacing: '0.15em', fontFamily: MONO }}>EXCLUDE (must NOT appear between)</Typography>
              <Typography sx={{ color: '#ef4444', fontSize: '0.95rem', fontWeight: 800, fontFamily: MONO }}>{excludeSignal}</Typography>
            </Box>
          </Stack>
          <Typography sx={{ color: alpha('#fff', 0.35), fontSize: '0.55rem', fontFamily: MONO, letterSpacing: '0.1em' }}>
            CONTEXT: {cTimeframe} · {cAsset} · {cSession}
          </Typography>
          <Button variant="contained" fullWidth onClick={handleSelect}
            sx={{
              bgcolor: '#f59e0b', color: '#000', fontWeight: 800, fontSize: '0.75rem',
              fontFamily: MONO, letterSpacing: '0.08em', borderRadius: 1.5, py: 0.9,
              textTransform: 'none',
              boxShadow: `0 4px 18px ${alpha('#f59e0b', 0.4)}`,
              '&:hover': { bgcolor: '#f59e0b', boxShadow: `0 4px 24px ${alpha('#f59e0b', 0.6)}` },
            }}>
            ✓ ADD FILTER
          </Button>
        </Paper>
      ) : (
        <Paper elevation={0} sx={{
          bgcolor: alpha('#000', 0.5), border: `1px solid ${alpha(ac, 0.2)}`,
          borderRadius: 2, p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <Box>
            <Typography sx={{ color: alpha('#fff', 0.2), fontSize: '0.5rem', letterSpacing: '0.15em', fontFamily: MONO }}>SIGNAL</Typography>
            <Typography sx={{ color: ac, fontSize: '1.05rem', fontWeight: 800, fontFamily: MONO }}>{signalName}</Typography>
            <Typography sx={{ color: alpha('#fff', 0.3), fontSize: '0.55rem', fontFamily: MONO, mt: 0.3 }}>
              {cTimeframe} · {cAsset} · {cSession}
            </Typography>
          </Box>
          <Button variant="contained" onClick={handleSelect}
            sx={{
              bgcolor: roleColor, color: '#000', fontWeight: 800, fontSize: '0.78rem',
              fontFamily: MONO, letterSpacing: '0.08em', borderRadius: 1.5, px: 3, py: 1,
              textTransform: 'none',
              boxShadow: `0 4px 18px ${alpha(roleColor, 0.4)}`,
              '&:hover': { bgcolor: roleColor, boxShadow: `0 4px 24px ${alpha(roleColor, 0.6)}` },
            }}>
            ✓ SELECT
          </Button>
        </Paper>
      )}
    </Box>
  );
}
